import React, { ReactElement, useEffect, useState } from "react";
import { useGetArticleTagsQuery, usePublishArticleMutation, useSaveArticleMutation } from "../../__generated__/api";
import useRouting from "../../contexts/Routing";
import Editor from "../agent-v3/editor/Editor";
import Avatar from "../avatar/Avatar";
import Confirm from "../confirm/Confirm";
import EditableControl, { ControlType } from "../editable-text/EditableText";

import { useTheme } from "../../contexts/Theme";
import Select from "../select/Select";
import RouterLink from "../theme/button/RouterLink";
import TimedMessage from "../timed-message/TimedMessage";
import Styles from "./CreateAnswer.module.scss";

const PUBLISH_OPTOIONS = [
    {
        label: "Publish Now",
        value: "publish",
        data: true,
    },
    {
        label: "Dont Publish",
        value: "done-publish",
        data: false,
    },
];
export default function CreateAnswerConfirm({
    title, text, plainText, isShowCreate, onHide, confirmMetricPath,
}: {
    title?: string;
    text: string;
    plainText?: string;
    isShowCreate: boolean;
    onHide: () => void;
    confirmMetricPath: string[];
}): ReactElement {
    const { Paths } = useRouting();
    const [createdId, setCreatedId] = useState<string | undefined>();

    const { data: tagsData } = useGetArticleTagsQuery();
    const theme = useTheme();

    const [articleTitle, setTitle] = useState(title || plainText?.createBlurbSingleLine(30));
    const [body, setBody] = useState(text);

    const [shouldPublish, setShouldPublish] = useState(true);
    const [tagIds, setTagIds] = useState<string[]>();

    const [saveArticle] = useSaveArticleMutation();
    const [publish] = usePublishArticleMutation();

    useEffect(() => {
        setCreatedId(undefined);
        setShouldPublish(true);
        setTitle(plainText?.createBlurbSingleLine(30));
        setBody(text);
        setTitle(title || plainText?.createBlurbSingleLine(30));
    }, [isShowCreate]);

    const onConfirm = async () => {
        const article = await saveArticle({
            variables: {
                article: {
                    title: articleTitle as string,
                    body,
                    tagIds,
                },
            },
        });
        const id = article.data?.saveArticle.id;
        setCreatedId(id);

        if (shouldPublish) {
            await publish({
                variables: {
                    id,
                    settings: {
                        publishDate: new Date(),
                    },
                },
            });
        }
    };

    return (
        <>
            <TimedMessage
                shouldShow={createdId != null}
                onClose={onHide}
                actionButton={(
                    <RouterLink
                        to={Paths.ANSWERS_GET}
                        params={{ id: createdId as string }}
                        variant="outline-gray"
                        size="small"
                        onClick={onHide}
                        target="_blank"
                        rel="noreferer"
                    >
                        View Answer
                    </RouterLink>
                )}
            >
                <div>
                    Your answer has been created
                </div>

            </TimedMessage>
            <Confirm
                shouldShow={(!createdId && isShowCreate) ?? false}
                onConfirm={!createdId ? onConfirm : undefined}
                size="xl"
                confirmText={!createdId ? "Save" : undefined}
                onHide={onHide}
                confirmMetricPath={confirmMetricPath}
                dialogClassName="mt-5"
            >
                <h4 className="mb-3">
                    <EditableControl
                        type={ControlType.TEXT}
                        value={articleTitle ?? ""}
                        onChange={value => setTitle(value as string)}
                        isEditing
                        inputClassName={theme.isV3 ? "form-control" : ""}
                    />
                </h4>

                <div className={Styles["create-answer-body"]}>
                    <Editor
                        onChange={change => setBody(change)}
                        value={body}
                        className={Styles.editor}
                    />
                </div>

                {tagsData?.articleTags?.isNotEmpty && (
                    <div className="mt-3">
                        <Select
                            isMulti
                            placeholder="Tags.."
                            onChange={options => setTagIds(options?.map(row => row.value))}
                            options={tagsData.articleTags.map(tag => ({
                                label: (
                                    <div>
                                        {tag.iconUrl ? <Avatar src={tag.iconUrl as string} variant="xs" /> : "#"}
                                        {tag.title}
                                    </div>
                                ),
                                value: tag.id,
                            }))}
                        />
                        <small className={theme.isV3 ? "" : "text-light mt-2"}>
                            Which tags do you want to add to this answer?
                        </small>
                    </div>
                )}

                <div className="mt-3">
                    <Select
                        minRequired={1}
                        placeholder="Select if to publish"
                        onChange={option => setShouldPublish(option?.data ?? true)}
                        options={PUBLISH_OPTOIONS}
                        value={PUBLISH_OPTOIONS.find(value => value.data === shouldPublish)}
                    />
                    <small className={theme.isV3 ? "" : "text-light mt-2"}>
                        Do you want to also publish the answer after creating or
                        just create the answer and allow you to manually publish
                        the answer later.
                    </small>
                </div>

            </Confirm>
        </>
    );
}

CreateAnswerConfirm.defaultProps = {
    plainText: undefined,
    title: undefined,
};
